const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

// Use minute output totals so daily-counter resets and larger display buckets do not affect the result.
export function findBestProductionHour(entries, from, to) {
  const points = entries.map((point) => ({ start: Date.parse(point.windowStart), metres: point.aggregateSpeed ?? 0 })).filter((point) => point.start >= from && point.start + MINUTE_MS <= to);
  const lastEnd = Math.min(to, (points[points.length - 1]?.start ?? from) + MINUTE_MS);
  let first = 0;
  let next = 0;
  let metres = 0;
  let best = null;

  for (let end = from + HOUR_MS; end <= lastEnd; end += MINUTE_MS) {
    const start = end - HOUR_MS;
    while (next < points.length && points[next].start < end) metres += points[next++].metres;
    while (first < next && points[first].start < start) metres -= points[first++].metres;
    if (metres > (best?.metres ?? 0)) best = { start, end, metres };
  }
  return best;
}

// Interpolate between existing chart entries without changing the graph's categorical time axis.
export function getProductionTimePosition(points, timestamp) {
  if (points.length < 2) return 0;
  let index = 0;
  let end = points.length;
  while (index < end) {
    const middle = (index + end) >>> 1;
    if (Date.parse(points[middle].date) >= timestamp) end = middle;
    else index = middle + 1;
  }
  if (index === points.length) return 1;
  if (index === 0) return 0;
  const previous = Date.parse(points[index - 1].date);
  const fraction = (timestamp - previous) / (Date.parse(points[index].date) - previous);
  return (index - 1 + fraction) / (points.length - 1);
}

export function getExcludedMinutes(start, end, gaps, noFlowEntries = []) {
  const excluded = gaps.reduce((sum, gap) => sum + Math.max(0, Math.min(end, gap.end) - Math.max(start, gap.start)), 0) / MINUTE_MS;
  return noFlowEntries.reduce((sum, entry) => {
    if (entry.aggregateSpeed !== 0 || !(entry.noFlowMinutes > 0)) return sum;
    const duration = entry.windowLength ?? 1;
    const overlapStart = Math.max(start, Date.parse(entry.windowStart));
    const overlapEnd = Math.min(end, Date.parse(entry.windowStart) + duration * MINUTE_MS);
    if (overlapEnd <= overlapStart) return sum;
    const remaining = Math.max(0, (overlapEnd - overlapStart) / MINUTE_MS - getExcludedMinutes(overlapStart, overlapEnd, gaps));
    return sum + remaining * Math.min(1, entry.noFlowMinutes / duration);
  }, excluded);
}

export function buildProductionGaps(entries, from, to, defaultGaps = [], stops = []) {
  const gaps = [];
  entries.forEach((point) => {
    if (point.aggregateSpeed !== 0) return;
    const start = Date.parse(point.windowStart);
    const end = start + (point.windowLength ?? 1) * MINUTE_MS;
    const previous = gaps[gaps.length - 1];
    if (previous?.end === start) previous.end = end;
    else gaps.push({ key: start, start, end });
  });
  return gaps
    .filter((gap) => gap.start < to && gap.end > from)
    .map((gap) => ({ ...gap, start: Math.max(from, gap.start), end: Math.min(to, gap.end) }))
    .flatMap((gap) => {
      if (!defaultGaps.length && !stops.length) return [gap];
      const boundaries = [...new Set([gap.start, gap.end, ...[...defaultGaps, ...stops].flatMap((range) => [range.start, range.end]).filter((time) => time > gap.start && time < gap.end)])].sort(
        (a, b) => a - b
      );
      return boundaries.slice(0, -1).map((start, index) => ({
        key: index === 0 ? gap.key : start,
        start,
        end: boundaries[index + 1],
        defaultExcluded: defaultGaps.some((range) => start >= range.start && start < range.end),
        hasStop: stops.some((stop) => start < stop.end && boundaries[index + 1] > stop.start)
      }));
    });
}

export function mergeProductionGaps(gaps) {
  const merged = [];
  [...gaps]
    .sort((a, b) => a.start - b.start)
    .forEach((gap) => {
      if (gap.end <= gap.start) return;
      const previous = merged[merged.length - 1];
      if (previous && gap.start <= previous.end) previous.end = Math.max(previous.end, gap.end);
      else merged.push({ key: gap.start, start: gap.start, end: gap.end });
    });
  return merged;
}

// Daily local-clock windows intersect recorded zero-output minutes only.
export function buildDefaultProductionGaps(entries, days = [], timeframes = [], stops = []) {
  if (!timeframes.length && !stops.some((stop) => stop.exclude)) return [];
  const windows = days.flatMap((day) => {
    const midnight = new Date(day.dayStart);
    const nextMidnight = new Date(midnight);
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    const resolveTime = (time) => {
      if (time === 'productionStart') return day.productionStart ? Date.parse(day.productionStart) : nextMidnight.getTime();
      if (time === 'productionEnd') return day.productionEnd ? Date.parse(day.productionEnd) + MINUTE_MS : midnight.getTime();
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date(midnight);
      date.setHours(hours, minutes, 0, 0);
      return date.getTime();
    };
    return timeframes.flatMap(({ from, to }) => {
      const start = resolveTime(from);
      const end = resolveTime(to);
      if (end >= start) return [{ start, end }];
      return [
        { start: midnight.getTime(), end },
        { start, end: nextMidnight.getTime() }
      ];
    });
  });
  windows.push(...stops.filter((stop) => stop.exclude));
  const zeroGaps = buildProductionGaps(entries, -Infinity, Infinity);
  return mergeProductionGaps(zeroGaps.flatMap((gap) => windows.map((window) => ({ start: Math.max(gap.start, window.start), end: Math.min(gap.end, window.end) }))));
}

export function applyProductionGapExclusions(dataByArea, dataset, gaps, entries, excludeNoFlow = false) {
  const points = dataByArea[dataset.key] ?? [];
  const last = points[points.length - 1];
  const start = Date.parse(points[0]?.date);
  const end = Date.parse(last?.date) + (last?.durationMinutes ?? 1) * MINUTE_MS;
  const activeGaps = gaps.filter((gap) => gap.start < end && gap.end > start);
  if (!activeGaps.length && !excludeNoFlow) return dataByArea;
  const removedEntries = entries.filter((entry) => {
    if (entry.aggregateSpeed !== 0) return false;
    const entryStart = Date.parse(entry.windowStart);
    const entryEnd = entryStart + (entry.windowLength ?? 1) * MINUTE_MS;
    return entryStart < end && entryEnd > start && ((excludeNoFlow && entry.noFlowMinutes > 0) || activeGaps.some((gap) => entryStart < gap.end && entryEnd > gap.start));
  });
  const noFlowEntries = excludeNoFlow ? removedEntries : [];
  if (!getExcludedMinutes(start, end, activeGaps, noFlowEntries)) return dataByArea;
  return {
    ...dataByArea,
    [dataset.key]: points.flatMap((point) => {
      const start = Date.parse(point.date);
      const duration = point.durationMinutes ?? 1;
      const end = start + duration * MINUTE_MS;
      const removedMinutes = getExcludedMinutes(start, end, activeGaps, noFlowEntries);
      if (!removedMinutes) return [point];
      const remainingMinutes = Math.max(0, duration - removedMinutes);
      if (!remainingMinutes) return [];
      const remainingRatio = remainingMinutes / duration;
      const removed = removedEntries
        .map((entry) => ({
          entry,
          ratio:
            getExcludedMinutes(
              Math.max(start, Date.parse(entry.windowStart)),
              Math.min(end, Date.parse(entry.windowStart) + (entry.windowLength ?? 1) * MINUTE_MS),
              activeGaps,
              excludeNoFlow ? [entry] : []
            ) / (entry.windowLength ?? 1)
        }))
        .filter((item) => item.ratio > 0);
      return [
        {
          ...point,
          durationMinutes: remainingMinutes,
          expectedSpeed: typeof point.expectedSpeed === 'number' ? point.expectedSpeed * remainingRatio : point.expectedSpeed,
          contractSpeed: typeof point.contractSpeed === 'number' ? point.contractSpeed * remainingRatio : point.contractSpeed,
          aggregatedBoolSignals:
            typeof point.aggregatedBoolSignals === 'number'
              ? Math.max(0, point.aggregatedBoolSignals - removed.reduce((sum, { entry, ratio }) => sum + (entry.aggregatedBoolSignals ?? 0) * ratio, 0))
              : point.aggregatedBoolSignals,
          errors: (point.errors ?? []).map((error) => ({
            ...error,
            minutes: Math.max(0, error.minutes - removed.reduce((sum, { entry, ratio }) => sum + (entry.errors?.find((item) => item.name === error.name)?.minutes ?? 0) * ratio, 0))
          }))
        }
      ];
    })
  };
}
